import { action, computed, makeObservable, observable } from "mobx";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import copy from "copy-to-clipboard";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import type {
    AISummaryResponse,
    SignalDataEndpoint,
    SignalsRequest,
} from "components/Signals/SignalsModels";
import { SignalsService } from "services/SignalsService";

interface AISummary {
    refreshable: boolean;
    outdated: boolean;
    summary?: string;
    feedback?: FeedbackType;
}

export enum FeedbackType {
    Dislike = 1,
    ItsOkay = 2,
    Like = 3,
}

class AISummaryStore extends BaseStore {
    private messageStore: MessageStore;
    private signalsService: SignalsService = new SignalsService();
    private timeoutErrorMessage =
        "Spotlight has timed out, click refresh to try again.";

    @observable
    isRefreshable: boolean = false;

    @observable
    summary: string = "";

    @observable
    isOutdated: boolean = false;

    @observable
    summaryRequestId: string | undefined = undefined;

    // boolean to control the first fetch of AI Summaries
    @observable isInitialSummaryFetched: boolean = false;

    @observable
    feedbackState: FeedbackType | undefined = undefined;
    @observable
    protectedDataBase64: string | undefined = undefined;

    private baseObjectId: string;

    constructor({
        baseObjectId,
        rootStore,
    }: {
        baseObjectId: string;
        rootStore: IRootStore;
    }) {
        super("AI Summaries Store");
        makeObservable(this);

        this.baseObjectId = baseObjectId;
        this.messageStore = rootStore.getStore(MessageStore);
    }

    @action
    handleSummaryResponse(response: AISummaryResponse) {
        if (response.result.successful) {
            this.updateSummary(response.result.summary);

            this.protectedDataBase64 = response.result.protectedDataBase64;
        } else {
            // this handles the failed summary request in the case a failure happens before the 60 second timeout
            this.updateSummary(this.timeoutErrorMessage);
        }

        this.summaryRequestId = undefined;
    }

    @action
    updateSummary(summary: string) {
        this.summary = this.transformSummary(summary);
        this.summaryRequestId = undefined;
    }

    @action
    transformSummary(summary: string) {
        return summary.replaceAll("\n\n", "\n");
    }

    @computed get isSummaryLoading() {
        return this.getTaskLoading("aiSummary-" + this.baseObjectId);
    }

    @action
    async refreshSummary(
        chartState: SignalsRequest,
        dataEndpoint: SignalDataEndpoint,
    ) {
        this.isOutdated = false;
        this.isRefreshable = false;
        this.feedbackState = undefined;
        // do things to make the refresh
        this.setupAsyncTask("aiSummary-" + this.baseObjectId, async () => {
            try {
                const result = await this.signalsService.refreshAISummary(
                    dataEndpoint,
                    chartState,
                );
                if (result.successful) {
                    this.summary = this.transformSummary(result.summary);
                    this.protectedDataBase64 = result.protectedDataBase64;
                } else {
                    this.summaryRequestId = undefined;
                    this.summary = this.timeoutErrorMessage;
                }
            } catch (error) {
                this.summary = this.timeoutErrorMessage;
                this.isRefreshable = true;
            }
        });
    }

    @action
    async refreshSummaryWithRowData(
        chartId: string,
        data: SignalReportDatum[],
        dataEndpoint: SignalDataEndpoint,
    ) {
        this.isOutdated = false;
        this.isRefreshable = false;
        this.feedbackState = undefined;

        this.setupAsyncTask("aiSummary-" + this.baseObjectId, async () => {
            try {
                const result =
                    await this.signalsService.refreshAISummaryWithRowData(
                        dataEndpoint,
                        data,
                    );
                if (result.successful) {
                    this.summary = this.transformSummary(result.summary);
                    this.protectedDataBase64 = result.protectedDataBase64;
                } else {
                    this.summaryRequestId = undefined;
                    this.summary = this.timeoutErrorMessage;
                }
            } catch (error) {
                this.summary = this.timeoutErrorMessage;
                this.isRefreshable = true;
            }
        });
    }

    @action
    async refreshSummaryCustom(
        chartId: string,
        getSummary: () => Promise<string>,
        options?: { errorMessage?: string; onError?: () => void },
    ) {
        this.isInitialSummaryFetched = true;
        this.isOutdated = false;
        this.isRefreshable = false;
        this.feedbackState = undefined;

        const taskName = "aiSummary-" + (chartId ?? this.baseObjectId);

        this.setupAsyncTask(taskName, async () => {
            try {
                const summary = await getSummary();
                this.summary = this.transformSummary(summary);
            } catch (error) {
                this.summary =
                    options?.errorMessage ?? this.timeoutErrorMessage;
                this.isRefreshable = true;
                options?.onError?.();
            }
        });
    }

    /**
     * Posts the provided feedback to the AISummaryFeedback endpoint. `feedbackStateByChartId` is
     * optimistically updated with the provided feedback state, in the event the feedback request
     * fails it is set back to undefined.
     * Loading state can be tracked via the task `Feedback-{chartId}`
     * @param chartId
     * @param feedbackType
     * @param feedback
     * @returns `feedbackType` on success and undefined on error.
     */
    @action
    async sendSummaryFeedback(
        chartId: string,
        feedbackType: FeedbackType,
        feedback: string | null = null,
    ) {
        this.feedbackState = feedbackType;

        return this.setupAsyncTask(`Feedback-${chartId}`, async () => {
            if (!this.protectedDataBase64) return undefined;

            try {
                await this.signalsService.aiSummaryFeedback(
                    this.protectedDataBase64,
                    feedbackType,
                    feedback,
                );
                return feedbackType;
            } catch (error) {
                this.feedbackState = undefined;
                return undefined;
            }
        });
    }

    @action
    setIsSummaryRefreshable(refreshable: boolean) {
        this.isRefreshable = refreshable;
    }

    @action
    setIsSummaryOutdated(outdated: boolean) {
        this.isOutdated = outdated;
    }

    copySummaryToClipboard() {
        copy(this.summary);
        this.messageStore.logInfo("Copied to clipboard");
    }

    getSummary(): AISummary {
        return {
            refreshable: this.isRefreshable,
            outdated: this.isOutdated,
            summary: this.summary,
            feedback: this.feedbackState,
        };
    }

    @action
    clearTimeoutErrors() {
        if (this.summary !== this.timeoutErrorMessage) return;

        this.summary = "";
    }
}

export default AISummaryStore;
